import { fromNullable, none, some } from 'fp-ts/lib/Option';
import Feature from 'ol/Feature';
import Map from 'ol/Map';
import { Extent } from 'ol/extent';
import Geometry from 'ol/geom/Geometry';
import { scopeOption } from '../../lib/scope';
import { tryTuple4 } from '../../util/index';
import type {
    ExtractFeature,
    ExtractOptions,
    InteractionExtract,
    MainLayerCollection,
} from '../types';
import { selectExtentInCollection, withInteraction } from '../functions';

interface FeatureExtract {
    lid: string;
    fs: Feature<Geometry>[];
}

interface MapAndLayers {
    map: Map;
    layers: MainLayerCollection;
}

interface MapAndLayersAndExtent extends MapAndLayers {
    extent: [number, number, number, number];
}

const getExtent = ({ map }: MapAndLayers) =>
    some(map.getView().calculateExtent(map.getSize()));

const sameExtent = (e1: Extent, e2: Extent) =>
    e1[0] === e2[0] && e1[1] === e2[1] && e1[2] === e2[2] && e1[3] === e2[3];

const fromExtract = ({ fs, lid }: FeatureExtract): ExtractFeature[] =>
    fs.map(f => ({
        layerId: lid,
        featureId: f.getId() ?? '__None__',
    }));

const extractFeatures = ({ layers, extent }: MapAndLayersAndExtent) =>
    selectExtentInCollection(layers, extent).reduce<ExtractFeature[]>(
        (acc, fe) => acc.concat(fromExtract(fe)),
        [],
    );

export const extract = ({ setCollection }: ExtractOptions) => {
    let mapRef: Map;
    let colRef: MainLayerCollection;
    let lastExtent: Extent = [0, 0, 0, 0];

    const update = withInteraction<InteractionExtract>(
        'extract',
        () =>
            scopeOption()
                .let('map', fromNullable(mapRef))
                .let('layers', fromNullable(colRef))
                .let('extent', e =>
                    getExtent(e).chain<[number, number, number, number]>(e =>
                        tryTuple4(e),
                    ),
                )
                .let('shouldUpdate', ({ extent }) => {
                    const su = !sameExtent(lastExtent, extent);
                    if (su) {
                        lastExtent = extent;
                        return some(true);
                    }
                    return none;
                })
                .map(extractFeatures)
                .map(setCollection),
        () => {
            lastExtent = [0, 0, 0, 0];
        },
    );

    const init = (map: Map, layers: MainLayerCollection) => {
        mapRef = map;
        colRef = layers;
    };

    return { init, update };
};
